import type { MockBridge } from "./mock-bridge";

export class DebugMenu {
  private container: HTMLElement;
  private isVisible = false;

  constructor(private mockBridge: MockBridge) {
    this.container = document.createElement("div");
    this.setupContainer();
    this.setupToggleButton();
    this.mockBridge.setOnNotificationChange(() => this.render());
    this.mockBridge.setOnStateChange(() => this.render());
    this.render();
    document.body.appendChild(this.container);
  }

  private setupContainer() {
    this.container.style.position = "fixed";
    this.container.style.top = "0";
    this.container.style.right = "0";
    this.container.style.width = "300px";
    this.container.style.background = "rgba(0, 0, 0, 0.9)";
    this.container.style.color = "white";
    this.container.style.padding = "10px";
    this.container.style.fontFamily = "monospace";
    this.container.style.fontSize = "12px";
    this.container.style.zIndex = "10000";
    this.container.style.maxHeight = "100vh";
    this.container.style.overflowY = "auto";
    this.container.style.display = "none";
  }

  private setupToggleButton() {
    const toggleBtn = document.createElement("button");
    toggleBtn.textContent = "Debug Menu";
    toggleBtn.style.position = "fixed";
    toggleBtn.style.top = "10px";
    toggleBtn.style.right = "10px";
    toggleBtn.style.zIndex = "10001";
    toggleBtn.style.padding = "5px 10px";
    toggleBtn.style.background = "#666";
    toggleBtn.style.color = "white";
    toggleBtn.style.border = "none";
    toggleBtn.style.borderRadius = "4px";
    toggleBtn.style.cursor = "pointer";

    toggleBtn.addEventListener("click", () => this.toggle());
    document.body.appendChild(toggleBtn);
  }

  private toggle() {
    this.isVisible = !this.isVisible;
    this.container.style.display = this.isVisible ? "block" : "none";
  }

  private createButton(text: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.textContent = text;
    button.style.margin = "5px";
    button.style.padding = "5px 10px";
    button.style.background = "#444";
    button.style.color = "white";
    button.style.border = "none";
    button.style.borderRadius = "4px";
    button.style.cursor = "pointer";
    button.addEventListener("click", onClick);
    return button;
  }

  private render() {
    this.container.innerHTML = "";

    // Player State Section
    const playerSection = document.createElement("div");
    playerSection.innerHTML = `
      <h3>Mock Player State</h3>
      <p style="color: #aaa; font-size: 11px;">Note: Changes to registration or entry payload will reload the game.</p>
    `;

    const currentPlayer = this.mockBridge.getMockPlayer();
    const playerInfo = document.createElement("pre");
    playerInfo.textContent = JSON.stringify(currentPlayer, null, 2);
    playerSection.appendChild(playerInfo);

    // Player Controls
    const playerControls = document.createElement("div");

    // Login/Logout button
    const loginBtn = this.createButton(
      currentPlayer.registered ? "Logout" : "Login",
      () => {
        if (
          confirm(
            `This will ${currentPlayer.registered ? "logout" : "login"} the player and reload the game. Continue?`,
          )
        ) {
          this.mockBridge.setMockPlayer({
            registered: !currentPlayer.registered,
          });
        }
      },
    );
    playerControls.appendChild(loginBtn);

    // Reset player button
    const resetBtn = this.createButton(
      "Reset Player (clears all state)",
      () => {
        if (
          confirm(
            "This will reset all player state and reload the game. Continue?",
          )
        ) {
          this.mockBridge.resetPlayer();
        }
      },
    );
    playerControls.appendChild(resetBtn);

    // Player ID change with reload warning
    const changeIdBtn = this.createButton(
      "Change Player ID (reloads game)",
      () => {
        const newId = prompt("Enter new player ID:", currentPlayer.playerId);
        if (newId && newId !== currentPlayer.playerId) {
          if (confirm("This will reload the game. Continue?")) {
            this.mockBridge.setMockPlayer({ playerId: newId });
          }
        }
      },
    );
    playerControls.appendChild(changeIdBtn);

    // Entry payload (with reload warning)
    const updatePayloadBtn = this.createButton(
      "Update Entry Payload (reloads game)",
      () => {
        try {
          const newPayload = prompt(
            "Enter new entry payload (as JSON):",
            JSON.stringify(currentPlayer.entryPayload),
          );
          if (newPayload) {
            const parsed = JSON.parse(newPayload) as Record<string, string>;
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("entryPayload", JSON.stringify(parsed));
            window.location.href = newUrl.toString();
          }
        } catch (_error) {
          alert("Invalid JSON payload");
        }
      },
    );
    playerControls.appendChild(updatePayloadBtn);

    // State manipulation controls
    const editStateBtn = this.createButton("Edit Player State", () => {
      try {
        const currentPlayerData = currentPlayer.playerData;
        const newPlayerData = prompt(
          "Edit player state (as JSON):",
          JSON.stringify(currentPlayerData, null, 2),
        );

        if (newPlayerData) {
          const parsed = JSON.parse(newPlayerData) as Record<string, unknown>;
          this.mockBridge.updatePlayerData({ update: parsed });
        }
      } catch (error) {
        alert(
          "Invalid JSON state: " +
            (error instanceof Error ? error.message : String(error)),
        );
      }
    });
    playerControls.appendChild(editStateBtn);

    playerSection.appendChild(playerControls);

    // Message Log Section
    const logSection = document.createElement("div");
    logSection.innerHTML = "<h3>Message Log</h3>";

    const messageLog = this.mockBridge.getMessageLog();
    const logInfo = document.createElement("pre");
    logInfo.style.maxHeight = "200px";
    logInfo.style.overflowY = "auto";
    logInfo.textContent = JSON.stringify(messageLog, null, 2);

    const clearLogButton = this.createButton("Clear Log", () => {
      this.mockBridge.clearMessageLog();
      this.render();
    });

    logSection.appendChild(clearLogButton);
    logSection.appendChild(logInfo);

    // Notifications Section
    const notificationsSection = document.createElement("div");
    notificationsSection.innerHTML = "<h3>Mock Notifications</h3>";

    const notifications = this.mockBridge.getStoredNotifications();
    const notificationsInfo = document.createElement("pre");
    notificationsInfo.style.maxHeight = "200px";
    notificationsInfo.style.overflowY = "auto";

    if (notifications.length === 0) {
      notificationsInfo.textContent = "No notifications scheduled";
    } else {
      notificationsInfo.textContent = JSON.stringify(notifications, null, 2);
    }

    const clearNotificationsBtn = this.createButton(
      "Clear Notifications",
      () => {
        if (confirm("Clear all scheduled notifications?")) {
          this.mockBridge.clearNotifications();
          this.render();
        }
      },
    );

    notificationsSection.appendChild(clearNotificationsBtn);
    notificationsSection.appendChild(notificationsInfo);

    // Onboardings Section
    const onboardingsSection = document.createElement("div");
    onboardingsSection.innerHTML = "<h3>Onboardings</h3>";

    // Reject Login Link Leases toggle
    const rejectLoginLeasesContainer = document.createElement("div");
    rejectLoginLeasesContainer.style.margin = "10px 0";

    const rejectLoginLeasesLabel = document.createElement("span");
    rejectLoginLeasesLabel.textContent = "Reject Login Link Leases: ";
    rejectLoginLeasesContainer.appendChild(rejectLoginLeasesLabel);

    const onButton = this.createButton("ON", () => {
      this.mockBridge.setRejectLoginLeaseAcquisition(true);
      this.render();
    });

    const offButton = this.createButton("OFF", () => {
      this.mockBridge.setRejectLoginLeaseAcquisition(false);
      this.render();
    });

    // Set active state styling
    if (this.mockBridge.getRejectLoginLeaseAcquisition()) {
      onButton.style.backgroundColor = "#6a6";
      offButton.style.backgroundColor = "#444";
    } else {
      onButton.style.backgroundColor = "#444";
      offButton.style.backgroundColor = "#6a6";
    }

    rejectLoginLeasesContainer.appendChild(onButton);
    rejectLoginLeasesContainer.appendChild(offButton);
    onboardingsSection.appendChild(rejectLoginLeasesContainer);

    // Skip login SMS toggle
    const skipLoginSmsContainer = document.createElement("div");
    skipLoginSmsContainer.style.margin = "10px 0";

    const skipLoginSmsLabel = document.createElement("span");
    skipLoginSmsLabel.textContent = "Skip login SMS: ";
    skipLoginSmsContainer.appendChild(skipLoginSmsLabel);

    const skipSmsOnButton = this.createButton("ON", () => {
      this.mockBridge.setSkipLoginSms(true);
      this.render();
    });

    const skipSmsOffButton = this.createButton("OFF", () => {
      this.mockBridge.setSkipLoginSms(false);
      this.render();
    });

    // Set active state styling
    if (this.mockBridge.getSkipLoginSms()) {
      skipSmsOnButton.style.backgroundColor = "#6a6";
      skipSmsOffButton.style.backgroundColor = "#444";
    } else {
      skipSmsOnButton.style.backgroundColor = "#444";
      skipSmsOffButton.style.backgroundColor = "#6a6";
    }

    skipLoginSmsContainer.appendChild(skipSmsOnButton);
    skipLoginSmsContainer.appendChild(skipSmsOffButton);
    onboardingsSection.appendChild(skipLoginSmsContainer);

    // Append all sections
    this.container.appendChild(playerSection);
    this.container.appendChild(notificationsSection);
    this.container.appendChild(onboardingsSection);
    this.container.appendChild(logSection);
  }
}
